import { RefinementCtx } from 'zod';
import { ContractorFormType, NameByValue } from '@/types';
import { AffiliationType, EmployeeRole, LocationPreference } from '@/api/apis/resources';
import { COUNTRY_SCHEMA, getPhoneSchema, addCustomZodErrors } from '@/utils/helpers';
import { getCountryHasStates } from '@/hooks/useCountrySubdivisionInfo';

export const createContractorFormValidation =
  (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  (
    {
      affiliationType,
      affiliationTxCenterId,
      affiliationOpoId,
      country,
      state,
      roles,
      organType,
      contractorCertifications,
      locationPreference,
      baseLocationId,
      tsaPrecheckNumber,
      phone,
    }: ContractorFormType,
    ctx: RefinementCtx,
  ) => {
    const addError = (path: string[], message: string) => addCustomZodErrors({ ctx, path, message });

    const addIf = (condition: boolean, path: string[], message: string) => {
      if (condition) addError(path, message);
    };
    const countryHasStates = getCountryHasStates(composedSubdivisionsList, country);
    const isStateValidation = countryHasStates && !state;
    const isAffiliationTxCenterIdValidation = affiliationType === AffiliationType.TXCenter && !affiliationTxCenterId;
    const isAffiliationOpoIdValidation = affiliationType === AffiliationType.OPO && !affiliationOpoId;

    const rolesForOrganValidation: EmployeeRole[] = [
      EmployeeRole.ClinicalSpecialist,
      EmployeeRole.RetrievalSurgeon,
      EmployeeRole.ClinicalLead,
    ];

    const isOrganValidation = roles.some((role) => rolesForOrganValidation.includes(role)) && !organType.length;
    const isInitialCertificationValidation = organType.length !== contractorCertifications?.length;
    const isBaseLocationValidation = locationPreference === LocationPreference.Base && !baseLocationId;

    const shouldValidateTsaPrecheck = tsaPrecheckNumber !== null && tsaPrecheckNumber?.trim() !== '';

    const phoneSchemaResult = getPhoneSchema({ isRequired: true }).safeParse(phone);
    const countrySchemaResult = COUNTRY_SCHEMA.safeParse(country);

    addIf(isAffiliationTxCenterIdValidation, ['affiliationTxCenterId'], 'TX Center is required');
    addIf(isAffiliationOpoIdValidation, ['affiliationOpoId'], 'OPO is required');

    if (!countrySchemaResult.success) {
      addError(['country'], countrySchemaResult.error.issues[0].message);
    }

    addIf(isStateValidation, ['state'], 'Select state');
    addIf(isOrganValidation, ['organType'], 'Select organ');
    addIf(isInitialCertificationValidation, ['contractorCertifications'], 'Select certification date');
    addIf(isBaseLocationValidation, ['baseLocationId'], 'Select base location');

    if (!phoneSchemaResult.success) {
      addError(['phone'], phoneSchemaResult.error.issues[0].message);
    }

    if (shouldValidateTsaPrecheck) {
      const validFormat = /^[a-zA-Z0-9]+$/.test(tsaPrecheckNumber);
      const validLength = tsaPrecheckNumber.length >= 9 && tsaPrecheckNumber.length <= 10;
      const validPrefix = /^(TT|TE|AC)/.test(tsaPrecheckNumber);

      if (!validFormat || !validLength || !validPrefix) {
        addError(
          ['tsaPrecheckNumber'],
          'TSA PreCheck number must be 9-10 alphanumeric characters and begin with TT, TE, or AC',
        );
      }
    }
  };
